// src/components/TaskModal/TaskForm.jsx
import React from 'react';
import { TASK_STATUS_ORDER, TASK_STATUS_LABELS, TASK_PRIORITY, TASK_PRIORITY_LABELS, MOCK_ASSIGNEES } from '../../constants/task.js';
import { useTaskForm } from '../../hooks/useTaskForm.js';

const TODAY = new Date().toISOString().split('T')[0];

function Field({ id, label, required, error, touched, children }) {
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required && <span className="req"> *</span>}
      </label>
      {children}
      {touched && error && (
        <div className="error-msg" role="alert" aria-live="polite">
          {error}
        </div>
      )}
    </div>
  );
}

export default function TaskForm({ initial = {}, onSubmit, onCancel, submitLabel = 'Create Task', onDelete }) {
  const { values, errors, touched, setValue, touchField, validateAll, isFieldError } = useTaskForm(initial);

  const handleSubmit = () => {
    if (validateAll()) onSubmit(values);
  };

  return (
    <>
      <div className="modal-body">
        <Field id="title" label="Title" required error={errors.title} touched={touched.title}>
          <input
            id="title"
            className={`form-input${isFieldError('title') ? ' error' : ''}`}
            value={values.title}
            placeholder="Task title..."
            onChange={(e) => setValue('title', e.target.value)}
            onBlur={() => touchField('title')}
            autoFocus
            maxLength={120}
            aria-required="true"
            aria-invalid={isFieldError('title')}
          />
        </Field>

        <Field id="description" label="Description">
          <textarea
            id="description"
            className="form-textarea"
            value={values.description}
            placeholder="Optional description..."
            onChange={(e) => setValue('description', e.target.value)}
          />
        </Field>

        <div className="field-row">
          <Field id="priority" label="Priority" required error={errors.priority} touched={touched.priority}>
            <select
              id="priority"
              className={`form-select${isFieldError('priority') ? ' error' : ''}`}
              value={values.priority}
              onChange={(e) => { setValue('priority', e.target.value); touchField('priority'); }}
              aria-required="true"
              aria-invalid={isFieldError('priority')}
            >
              <option value="">Select priority...</option>
              {Object.entries(TASK_PRIORITY_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </Field>

          <Field id="status" label="Status">
            <select
              id="status"
              className="form-select"
              value={values.status}
              onChange={(e) => setValue('status', e.target.value)}
            >
              {TASK_STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="field-row">
          <Field id="assignee" label="Assignee">
            <select
              id="assignee"
              className="form-select"
              value={values.assignee}
              onChange={(e) => setValue('assignee', e.target.value)}
            >
              <option value="">Unassigned</option>
              {MOCK_ASSIGNEES.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </Field>

          <Field id="dueDate" label="Due Date" error={errors.dueDate} touched={touched.dueDate}>
            <input
              id="dueDate"
              type="date"
              className={`form-input${isFieldError('dueDate') ? ' error' : ''}`}
              value={values.dueDate}
              min={TODAY}
              onChange={(e) => setValue('dueDate', e.target.value)}
              onBlur={() => touchField('dueDate')}
              aria-invalid={isFieldError('dueDate')}
            />
          </Field>
        </div>
      </div>

      <div className="modal-footer">
        {onDelete ? (
          <button className="btn btn-danger btn-sm" onClick={onDelete} type="button">
            Delete Task
          </button>
        ) : (
          <span />
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onCancel} type="button">
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} type="button">
            {submitLabel}
          </button>
        </div>
      </div>
    </>
  );
}
